import { sendEmailVerification, sendPasswordResetEmail } from './lib/firebase';
import { apiBase as API_BASE, environmentName } from './environment';
import React, { useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  applicantGoogleProvider,
  applicantMicrosoftProvider,
  auth,
} from './lib/firebase';



const HOSTED_DEV = environmentName === 'dev';

const EMPTY_FORM = Object.freeze({
  legalBusinessName: '',
  dbaName: '',
  adminName: '',
  phone: '',
  businessAddress: '',
  usdotNumber: '',
  mcNumber: '',
  serviceRegions: '',
  serviceLanes: '',
  operatingHours: '',
  rateNotes: '',
  twicCapable: false,
  liveTrackingEnabled: true,
  photoProofEnabled: true,
  signatureProofEnabled: true,
  commercialAutoPolicyActive: false,
  authorityOwnershipCertified: false,
  consentAccepted: false,
});

async function responseBody(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.message || body?.error || `Request failed (${response.status})`);
    error.body = body;
    throw error;
  }
  return body;
}

async function authenticatedRequest(user, path, options = {}) {
  const token = await user.getIdToken(true);
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
}

function PartnerBrandHeader() {
  return (
    <header className="portal-brand">
      <a className="brand-link" href="/partner/apply">
        <span className="brand-mark">CC</span>
        <span><strong>ChemCab</strong><small>Partner Network</small></span>
      </a>
      <div className="brand-actions">
        <a className="portal-entry-link" href="/dashboard">Driver Portal</a>
        <a className="support-link" href="mailto:partners@chemcab.com">Partner help</a>
      </div>
    </header>
  );
}

function statusLabel(value) {
  return String(value || 'submitted').replaceAll('_', ' ');
}

function verificationMessage(reason) {
  const messages = {
    usdot_not_found_or_mismatched: 'The USDOT number could not be matched to the returned FMCSA carrier.',
    docket_not_linked_to_usdot: 'The MC number is not linked to that USDOT record.',
    legal_name_unavailable: 'FMCSA did not return a legal business name.',
    carrier_not_allowed_to_operate: 'The carrier is not currently allowed to operate or is out of service.',
    broker_only_authority_not_carrier_authority: 'Broker-only authority does not qualify as motor-carrier authority.',
    active_property_carrier_authority_not_confirmed: 'Active property-carrier common or contract authority was not confirmed.',
  };
  return messages[reason] || String(reason || '').replaceAll('_', ' ');
}

export default function PartnerApplicationPage() {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [user, setUser] = useState(auth.currentUser);
  const [authReady, setAuthReady] = useState(false);
  const [authMode, setAuthMode] = useState(HOSTED_DEV ? 'signin' : 'create');
  const [credentials, setCredentials] = useState({ email: '', password: '', confirm: '' });
  const [application, setApplication] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [editing, setEditing] = useState(true);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [verification, setVerification] = useState(null);
  const [verificationReceiptId, setVerificationReceiptId] = useState('');
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    fetch(`${API_BASE}/api/public/broker-partner/config`)
      .then(responseBody)
      .then(setConfig)
      .catch((nextError) => setConfigError(String(nextError?.message || nextError)));
  }, []);

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser);
    setAuthReady(true);
  }), []);

  useEffect(() => {
    if (!user?.emailVerified || config?.partnerSignupEnabled !== true) return;
    let active = true;
    setApplicationLoading(true);
    authenticatedRequest(user, '/api/applicant/broker-partner-application')
      .then(async (response) => {
        if (response.status === 404) return { application: null };
        return responseBody(response);
      })
      .then((body) => {
        if (!active) return;
        const next = body?.application || null;
        setApplication(next);
        if (next) {
          setForm((current) => ({
            ...current,
            legalBusinessName: next.legalBusinessName || '',
            dbaName: next.dbaName || '',
            adminName: next.adminName || '',
            phone: next.phone || '',
            businessAddress: next.businessAddress || '',
            usdotNumber: next.usdotNumber || '',
            mcNumber: next.mcNumber || '',
            serviceRegions: next.serviceRegions || '',
            serviceLanes: next.serviceLanes || '',
            operatingHours: next.operatingHours || '',
            rateNotes: next.rateNotes || '',
            twicCapable: next.capabilities?.twicCapable === true,
            liveTrackingEnabled: next.capabilities?.liveTrackingEnabled !== false,
            photoProofEnabled: next.capabilities?.photoProofEnabled !== false,
            signatureProofEnabled: next.capabilities?.signatureProofEnabled !== false,
            commercialAutoPolicyActive: next.capabilities?.commercialAutoPolicyActive === true,
          }));
          setEditing(false);
        }
      })
      .catch((nextError) => {
        if (active) setError(String(nextError?.message || nextError));
      })
      .finally(() => {
        if (active) setApplicationLoading(false);
      });
    return () => { active = false; };
  }, [config?.partnerSignupEnabled, user]);

  const verificationReasons = useMemo(
    () => (Array.isArray(verification?.failureReasons) ? verification.failureReasons : []),
    [verification]
  );

  const authenticate = async (event) => {
    event.preventDefault();
    setBusy(true); setError(''); setNotice('');
    try {
      const email = credentials.email.trim().toLowerCase();
      if (authMode === 'create') {
        if (credentials.password.length < 8) throw new Error('Use at least 8 characters for your password.');
        if (credentials.password !== credentials.confirm) throw new Error('Passwords do not match.');
        const result = await createUserWithEmailAndPassword(auth, email, credentials.password);
        await sendEmailVerification(result.user);
        setNotice(`Verification email sent to ${email}.`);
      } else {
        await signInWithEmailAndPassword(auth, email, credentials.password);
      }
    } catch (nextError) {
      const code = String(nextError?.code || '');
      setError(code.includes('email-already-in-use')
        ? 'An account already exists for this email. Sign in to continue the same application.'
        : String(nextError?.message || nextError));
    } finally { setBusy(false); }
  };

  const providerLogin = async (provider) => {
    setBusy(true); setError(''); setNotice('');
    try {
      await signInWithPopup(auth, provider);
    } catch (nextError) {
      const code = String(nextError?.code || '');
      setError(code.includes('account-exists-with-different-credential')
        ? 'This email already has a ChemCab login. Sign in with its existing method so the accounts are not duplicated.'
        : String(nextError?.message || nextError));
    } finally { setBusy(false); }
  };

  const verifyAuthority = async () => {
    if (!user) return;
    setVerifying(true); setError(''); setNotice(''); setVerification(null); setVerificationReceiptId('');
    try {
      const response = await authenticatedRequest(user, '/api/public/broker-partner-authority/verify', {
        method: 'POST',
        body: JSON.stringify({ usdotNumber: form.usdotNumber, mcNumber: form.mcNumber }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok && response.status !== 422) {
        throw new Error(body?.message || body?.error || `Verification failed (${response.status})`);
      }
      setVerification(body.verification || null);
      setVerificationReceiptId(body.receiptId || '');
      if (body.verified === true) {
        setForm((current) => ({
          ...current,
          legalBusinessName: body.verification?.legalName || current.legalBusinessName,
          dbaName: body.verification?.dbaName || current.dbaName,
          businessAddress: current.businessAddress || [
            body.verification?.physicalAddress?.street,
            body.verification?.physicalAddress?.city,
            body.verification?.physicalAddress?.state,
            body.verification?.physicalAddress?.zip,
          ].filter(Boolean).join(', '),
        }));
        setNotice(config?.devModeEnabled
          ? 'Development authority fixture accepted. This is test data, not an FMCSA verification.'
          : 'FMCSA confirmed the USDOT number, linked MC number, and active motor-carrier authority.');
      } else {
        setError('FMCSA did not confirm this carrier for the ChemCab Partner Network. Review the findings below.');
      }
    } catch (nextError) {
      setError(String(nextError?.message || nextError));
    } finally { setVerifying(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!user) return;
    setBusy(true); setError(''); setNotice('');
    try {
      if (verification?.verified !== true || !verificationReceiptId) {
        throw new Error('Verify the USDOT and MC authority with FMCSA before submitting.');
      }
      const path = application
        ? '/api/applicant/broker-partner-application'
        : '/api/public/broker-partner-applications';
      const response = await authenticatedRequest(user, path, {
        method: application ? 'PATCH' : 'POST',
        body: JSON.stringify({
          ...form,
          email: user.email,
          authorityVerificationReceiptId: verificationReceiptId,
          source: new URLSearchParams(window.location.search).get('source') || 'direct',
          sourceUrl: document.referrer || window.location.href,
        }),
      });
      const body = await responseBody(response);
      setApplication(body.application);
      setEditing(false);
      setNotice(application ? 'Your Partner application was updated and returned for review.' : 'Your Partner application was submitted.');
    } catch (nextError) {
      setError(String(nextError?.message || nextError));
    } finally { setBusy(false); }
  };

  if (!config && !configError) {
    return <div className="public-page"><PartnerBrandHeader /><main className="public-card centered-card">Opening Partner signup…</main></div>;
  }
  if (configError) {
    return <div className="public-page"><PartnerBrandHeader /><main className="public-card centered-card"><h1>Partner signup is unavailable.</h1><p>{configError}</p></main></div>;
  }
  if (config?.partnerSignupEnabled !== true) {
    return <div className="public-page"><PartnerBrandHeader /><main className="application-layout"><section className="application-intro"><p className="eyebrow">ChemCab Partner Network</p><h1>Carrier onboarding is being prepared.</h1><p>The application stays closed until FMCSA verification and the isolated Partner review workflow are enabled.</p></section><section className="public-card"><h2>Primary qualification</h2><p>Applicants will need a verifiable USDOT number and active MC motor-carrier authority.</p><a className="primary-button full-button partner-inline-link" href="mailto:partners@chemcab.com">Contact Partner Operations</a></section></main></div>;
  }
  if (!authReady) {
    return <div className="public-page"><PartnerBrandHeader /><main className="public-card centered-card">Checking your secure account…</main></div>;
  }

  if (!user) {
    return (
      <div className="public-page"><PartnerBrandHeader />
        <main className="application-layout">
          <section className="application-intro">
            <p className="eyebrow">ChemCab Partner Network</p><h1>Bring your carrier network to ChemCab.</h1>
            <p>Create one verified account for your company. ChemCab validates your USDOT and MC authority directly with FMCSA before accepting the application.</p>
            <div className="trust-list"><span>FMCSA verification</span><span>One account per email</span><span>Human review</span></div>
          </section>
          <form className="public-card application-form applicant-account-card" onSubmit={authenticate}>
            <div className="step-heading"><span>1</span><div><h2>{authMode === 'create' ? 'Create company administrator account' : 'Sign in to your application'}</h2><p>Use the email that should administer your Partner account.</p></div></div>
            {HOSTED_DEV ? <p className="staff-note">Use a seeded development partner account email and password.</p> : <><div className="applicant-sso-grid"><button className="sso-button google-sso" type="button" disabled={busy} onClick={() => providerLogin(applicantGoogleProvider)}>Continue with Google</button><button className="sso-button microsoft-sso" type="button" disabled={busy} onClick={() => providerLogin(applicantMicrosoftProvider)}>Continue with Microsoft</button></div>
            <div className="login-divider"><span>or use email and password</span></div></>}
            <label className="field"><span>Administrator email</span><input required type="email" autoComplete="email" value={credentials.email} onChange={(event) => setCredentials((current) => ({ ...current, email: event.target.value }))} /></label>
            <label className="field"><span>Password</span><input required minLength="8" type="password" autoComplete={authMode === 'create' ? 'new-password' : 'current-password'} value={credentials.password} onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))} /></label>
            {authMode === 'create' ? <label className="field"><span>Confirm password</span><input required minLength="8" type="password" autoComplete="new-password" value={credentials.confirm} onChange={(event) => setCredentials((current) => ({ ...current, confirm: event.target.value }))} /></label> : null}
            {notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}
            <button className="primary-button full-button" disabled={busy}>{busy ? 'Please wait…' : authMode === 'create' ? 'Create account' : 'Sign in'}</button>
            <button className="text-button full-button" type="button" onClick={() => { setAuthMode(authMode === 'create' ? 'signin' : 'create'); setError(''); setNotice(''); }}>{authMode === 'create' ? 'Already started? Sign in' : 'New partner? Create an account'}</button>
            {authMode === 'signin' ? <button className="reset-link" type="button" onClick={async () => { try { await sendPasswordResetEmail(auth, credentials.email.trim().toLowerCase()); setNotice('Password reset email sent.'); } catch (nextError) { setError(String(nextError?.message || nextError)); } }}>Forgot password?</button> : null}
          </form>
        </main>
      </div>
    );
  }

  if (!user.emailVerified) {
    return <div className="public-page"><PartnerBrandHeader /><main className="public-card success-card verification-card"><span className="success-check">✉</span><p className="eyebrow">Verify administrator email</p><h1>Check your inbox.</h1><p>Open the verification link sent to <strong>{user.email}</strong>, then return here.</p>{notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}<button className="primary-button full-button" onClick={async () => { setBusy(true); try { await user.reload(); if (!auth.currentUser?.emailVerified) throw new Error('Your email is not verified yet.'); await auth.currentUser.getIdToken(true); setUser(auth.currentUser); } catch (nextError) { setError(String(nextError?.message || nextError)); } finally { setBusy(false); } }}>{busy ? 'Checking…' : 'I verified my email'}</button><button className="text-button full-button" onClick={() => sendEmailVerification(user).then(() => setNotice('Verification email sent again.')).catch((nextError) => setError(String(nextError?.message || nextError)))}>Resend verification</button><button className="text-button full-button" onClick={() => signOut(auth)}>Use another account</button></main></div>;
  }

  if (applicationLoading) return <div className="public-page"><PartnerBrandHeader /><main className="public-card centered-card">Loading your Partner application…</main></div>;

  if (application && !editing) {
    const rejected = application.status === 'rejected';
    const developmentOnly = application.authorityVerification?.developmentOnly === true;
    return <div className="public-page"><PartnerBrandHeader /><main className="public-card success-card application-status-card">{developmentOnly ? <div className="partner-dev-banner"><strong>Development mode</strong><span>This application contains synthetic authority data and cannot represent production approval.</span></div> : null}<span className="success-check">{rejected ? '↻' : '✓'}</span><p className="eyebrow">Partner application {statusLabel(application.status)}</p><h1>{application.legalBusinessName}</h1><p>{rejected || application.status === 'changes_requested' ? 'Review the feedback, reverify your authority, update the application, and resubmit.' : 'ChemCab Partner Operations can now review your carrier authority, capabilities, lanes, and operating details.'}</p>{application.applicantFeedback ? <div className="form-error"><strong>Partner Operations feedback</strong><br />{application.applicantFeedback}</div> : null}{notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}<div className="reference-box"><span>Application reference</span><strong>{application.applicationId}</strong></div><section className="authority-result authority-result-verified"><div><span>{developmentOnly ? 'Development authority fixture' : 'FMCSA authority'}</span><strong>{application.usdotNumber} · {application.mcNumber}</strong><small>{application.authorityVerification?.legalName}</small></div><b>{developmentOnly ? 'Test data' : 'Verified'}</b></section>{application.editable ? <button className="primary-button full-button" onClick={() => { setEditing(true); setVerification(null); setVerificationReceiptId(''); setNotice('Reverify the USDOT and MC numbers before saving changes.'); }}>{rejected ? 'Update and reapply' : 'Edit application'}</button> : <div className="locked-note">This application is locked while ChemCab completes the current review step.</div>}<button className="text-button full-button" onClick={() => signOut(auth)}>Sign out</button></main></div>;
  }

  return (
    <div className="public-page"><PartnerBrandHeader />
      <main className="application-layout partner-application-layout">
        <section className="application-intro">{config?.devModeEnabled ? <div className="partner-dev-banner"><strong>Development mode is active</strong><span>The authority step uses a synthetic carrier fixture. Authentication and application ownership checks still apply.</span></div> : null}<p className="eyebrow">Carrier application</p><h1>Authority first. Operations second.</h1><p>{config?.devModeEnabled ? 'Use the development USDOT and MC pair below to exercise the complete application and review workflow.' : 'Enter the exact USDOT and MC numbers registered to your company. The legal name shown by FMCSA becomes the verified identity for this application.'}</p><div className="trust-list"><span>{user.email}</span><span>{config?.devModeEnabled ? 'Synthetic authority fixture' : 'Official FMCSA lookup'}</span><span>{config?.devModeEnabled ? 'Non-production only' : 'No self-attested authority'}</span></div></section>
        <form className="public-card application-form" onSubmit={submit}>
          <div className="step-heading"><span>1</span><div><h2>Verify operating authority</h2><p>A matching USDOT number and active MC common or contract motor-carrier authority are required.</p></div></div>
          <div className="field-grid"><label className="field"><span>USDOT number</span><input required inputMode="numeric" placeholder="1234567" value={form.usdotNumber} onChange={(event) => { set('usdotNumber', event.target.value); setVerification(null); setVerificationReceiptId(''); }} /></label><label className="field"><span>MC number</span><input required placeholder="MC-123456" value={form.mcNumber} onChange={(event) => { set('mcNumber', event.target.value); setVerification(null); setVerificationReceiptId(''); }} /></label></div>
          {config?.devModeEnabled && config?.devTestAuthority ? <button className="text-button full-button partner-dev-fill" type="button" onClick={() => { setForm((current) => ({ ...current, usdotNumber: config.devTestAuthority.usdotNumber || '', mcNumber: config.devTestAuthority.mcNumber || '' })); setVerification(null); setVerificationReceiptId(''); }}>Load development authority numbers</button> : null}
          <button className="secondary-button full-button" type="button" disabled={verifying || !form.usdotNumber || !form.mcNumber} onClick={verifyAuthority}>{verifying ? (config?.devModeEnabled ? 'Checking development fixture…' : 'Checking FMCSA…') : (config?.devModeEnabled ? 'Accept development authority fixture' : 'Verify USDOT and MC authority')}</button>
          {verification ? <section className={`authority-result ${verification.verified ? 'authority-result-verified' : 'authority-result-failed'}`}><div><span>{verification.developmentOnly ? 'Development authority fixture' : verification.verified ? 'FMCSA verified carrier' : 'FMCSA verification did not pass'}</span><strong>{verification.legalName || 'Carrier name unavailable'}</strong><small>{verification.usdotNumber} · {verification.docketNumber}</small></div><b>{verification.developmentOnly ? 'Test data' : verification.verified ? 'Verified' : 'Review'}</b>{verificationReasons.length ? <ul>{verificationReasons.map((reason) => <li key={reason}>{verificationMessage(reason)}</li>)}</ul> : null}{verification.sourceLinks?.safer ? <a href={verification.sourceLinks.safer} target="_blank" rel="noreferrer">Open official SAFER record</a> : null}</section> : null}
          <div className="section-divider" />
          <div className="step-heading"><span>2</span><div><h2>Company administrator</h2><p>The verified FMCSA legal name is read-only after lookup.</p></div></div>
          <div className="field-grid"><label className="field field-wide"><span>Verified legal business name</span><input required readOnly value={form.legalBusinessName} placeholder="Verify authority to populate" /></label><label className="field"><span>DBA name</span><input value={form.dbaName} onChange={(event) => set('dbaName', event.target.value)} /></label><label className="field"><span>Administrator name</span><input required autoComplete="name" value={form.adminName} onChange={(event) => set('adminName', event.target.value)} /></label><label className="field"><span>Administrator email</span><input readOnly value={user.email || ''} /></label><label className="field"><span>Phone</span><input required type="tel" autoComplete="tel" value={form.phone} onChange={(event) => set('phone', event.target.value)} /></label><label className="field field-wide"><span>Business address</span><input required autoComplete="street-address" value={form.businessAddress} onChange={(event) => set('businessAddress', event.target.value)} /></label></div>
          <div className="section-divider" />
          <div className="step-heading"><span>3</span><div><h2>Service and capabilities</h2><p>Describe where, when, and how your company can perform Broker work.</p></div></div>
          <label className="field"><span>Service regions and states</span><textarea required rows="3" placeholder="Texas, Louisiana, Oklahoma…" value={form.serviceRegions} onChange={(event) => set('serviceRegions', event.target.value)} /></label><label className="field"><span>Primary interstate lanes</span><textarea rows="3" placeholder="Houston ↔ Lake Charles; Dallas ↔ Shreveport…" value={form.serviceLanes} onChange={(event) => set('serviceLanes', event.target.value)} /></label><label className="field"><span>Operating hours and availability</span><textarea rows="3" placeholder="24/7 dispatch; overnight requests require 2 hours notice…" value={form.operatingHours} onChange={(event) => set('operatingHours', event.target.value)} /></label><label className="field"><span>Rate notes</span><textarea rows="3" placeholder="Base, per-mile, minimum, wait-time, after-hours…" value={form.rateNotes} onChange={(event) => set('rateNotes', event.target.value)} /></label>
          <div className="choice-stack"><label className="choice-row"><input type="checkbox" checked={form.commercialAutoPolicyActive} onChange={(event) => set('commercialAutoPolicyActive', event.target.checked)} /><span>Active commercial auto policy</span></label><label className="choice-row"><input type="checkbox" checked={form.twicCapable} onChange={(event) => set('twicCapable', event.target.checked)} /><span>TWIC-capable drivers available</span></label><label className="choice-row"><input type="checkbox" checked={form.liveTrackingEnabled} onChange={(event) => set('liveTrackingEnabled', event.target.checked)} /><span>Live tracking enabled</span></label><label className="choice-row"><input type="checkbox" checked={form.photoProofEnabled} onChange={(event) => set('photoProofEnabled', event.target.checked)} /><span>Photo proof enabled</span></label><label className="choice-row"><input type="checkbox" checked={form.signatureProofEnabled} onChange={(event) => set('signatureProofEnabled', event.target.checked)} /><span>Signature proof enabled</span></label></div>
          <label className="choice-row consent-row"><input required type="checkbox" checked={form.authorityOwnershipCertified} onChange={(event) => set('authorityOwnershipCertified', event.target.checked)} /><span>I certify that I am authorized to apply on behalf of the FMCSA carrier shown above.</span></label><label className="choice-row consent-row"><input required type="checkbox" checked={form.consentAccepted} onChange={(event) => set('consentAccepted', event.target.checked)} /><span>I confirm this information is accurate and consent to ChemCab verifying the company’s authority and qualifications.</span></label>
          {error ? <div className="form-error">{error}</div> : null}{notice ? <div className="form-success">{notice}</div> : null}
          <button className="primary-button full-button" disabled={busy || verification?.verified !== true}>{busy ? 'Submitting securely…' : application ? 'Update and resubmit application' : 'Submit Partner application'}</button>{application ? <button className="text-button full-button" type="button" onClick={() => setEditing(false)}>Cancel editing</button> : null}
        </form>
      </main>
    </div>
  );
}
